export const getCollection = async function (collectionRef) {
    const snapshot = await collectionRef.get();
    const docs = [];
    snapshot.docs.forEach((doc) => {
        docs.push({ id: doc.id, ...doc.data() });
    });
    return docs;
};
export const getDocument = async function (docRef) {
    const snapshot = await docRef.get();
    const data = snapshot.data();
    if (data && !data.id)
        data.id = snapshot.id;
    return data;
};
export const deleteQueryBatch = async function deleteQueryBatch(db, query) {
    const snapshot = await query.get();
    const batchSize = snapshot.size;
    if (batchSize === 0) {
        // When there are no documents left, we are done
        return;
    }
    // Delete documents in a batch
    const batch = db.batch();
    snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
    });
    await batch.commit();
    // Recurse on the next process tick, to avoid
    // exploding the stack.
    process.nextTick(() => {
        deleteQueryBatch(db, query);
    });
};
/**
 * Converts a Date object to a Firestore Timestamp, or returns the value unchanged
 * if it's not a Date object (e.g., already a Timestamp or null/undefined)
 *
 * @param dateValue - The value to convert (Date, Timestamp, null, undefined, etc.)
 * @returns Firestore Timestamp if input was a Date, otherwise returns the original value
 */
export function toFirestoreTimestamp(firestore, dateValue) {
    // If dateValue is null or undefined, return it unchanged
    if (dateValue === null || dateValue === undefined) {
        return dateValue;
    }
    // If dateValue is a Date object, convert to Firestore Timestamp
    if (dateValue instanceof Date) {
        return firestore.timestamp.fromDate(dateValue);
    }
    // Otherwise, return the original value unchanged
    return dateValue;
}
/**
 * Converts common date fields to Firestore Timestamps
 * Checks for startTime, finishTime, createdAt, updatedAt, startedAt, endedAt
 * and converts them if they exist and are Date objects
 *
 * @param obj - The object with potential date fields
 * @returns New object with date fields converted to Firestore Timestamps
 */
export function convertDatesToFirestore(firestore, obj) {
    const result = { ...obj };
    // List of common date field names to check and convert
    const dateFields = [
        "startTime",
        "finishTime",
        "createdAt",
        "updatedAt",
        "startedAt",
        "endedAt",
    ];
    dateFields.forEach((fieldName) => {
        if (fieldName in obj) {
            // Field exists in object
            if (obj[fieldName] === null) {
                // Keep null values as null
                result[fieldName] = null;
            }
            else {
                // Convert to Firestore timestamp if it's a Date, otherwise keep as-is
                result[fieldName] = toFirestoreTimestamp(firestore, obj[fieldName]);
            }
        }
        // If field is not defined in obj, ignore it (don't add to result)
    });
    return result;
}
/**
 * Recursively converts any explicit `undefined` property values to `null`
 * so the object can be safely written to Firestore without triggering
 * "Unsupported field value: undefined" errors.
 */
export function replaceUndefinedWithNull(obj) {
    if (obj === null || obj === undefined) {
        return null;
    }
    if (Array.isArray(obj)) {
        return obj.map(replaceUndefinedWithNull);
    }
    if (typeof obj === "object" && !(obj instanceof Date)) {
        if ("toMillis" in obj || "isEqual" in obj) {
            return obj;
        }
        const copy = {};
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            copy[key] = val === undefined ? null : replaceUndefinedWithNull(val);
        }
        return copy;
    }
    return obj;
}
